/**
 * Verification — the SM-13c staff review queue (GET /admin/verification +
 * POST /admin/verification/:id). Selfies arrive as staff-signed media URLs
 * (the media gate allows staff on verification_selfie). Approve / reject on
 * pending; revoke on verified — same decisions the endpoint always took.
 */
import { useState } from "react";
import { copy } from "@humble/shared";
import { Button, Card, Skeleton, toast } from "../../design/index.js";
import { isApiError } from "../../lib/api.js";
import { rawApi } from "../safety/raw-api.js";
import { useLoad } from "./use-load.js";

const C = copy.admin.verification;

interface VerificationRow {
  id: string;
  user_id: string;
  display_name: string | null;
  user_verification: string;
  pose_key: string;
  media_id: string | null;
  media_url: string | null;
  status: string;
  reason: string | null;
  score: number | null;
  created_at: string;
  resolved_at: string | null;
}

export function VerificationSection() {
  const [status, setStatus] = useState<"pending" | "verified" | "rejected">("pending");
  const { data: rows, loading, error, reload } = useLoad<VerificationRow[]>(
    `/admin/verification?status=${status}`,
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const decide = async (id: string, decision: "approve" | "reject" | "revoke") => {
    setBusyId(id);
    try {
      const reason = reasons[id]?.trim();
      await rawApi("POST", `/admin/verification/${id}`, {
        decision,
        ...(reason ? { reason } : {}),
      });
      toast(`${decision} — done.`, "success");
      reload();
    } catch (err) {
      toast(isApiError(err) ? err.message : copy.admin.action_failed, "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-verification">
      <div className="admin__filters">
        <label className="admin__filter">
          Status{" "}
          <select value={status} onChange={(e) => setStatus(e.target.value as never)}>
            <option value="pending">pending</option>
            <option value="verified">verified</option>
            <option value="rejected">rejected</option>
          </select>
        </label>
      </div>

      {error && (
        <Card padding="lg" className="admin__error">
          <p>{error}</p>
          <Button size="sm" variant="primary" onClick={reload}>
            Retry
          </Button>
        </Card>
      )}
      {loading && !error && (
        <div className="admin__list" aria-busy="true">
          <Skeleton height={140} radius={18} />
          <Skeleton height={140} radius={18} />
        </div>
      )}

      {!loading && !error && (
        <div className="admin__list">
          {(rows ?? []).map((v) => (
            <Card key={v.id} padding="md" className="admin__verif">
              {v.media_url ? (
                <img className="admin__selfie" src={v.media_url} alt={`Verification selfie — pose: ${v.pose_key}`} />
              ) : (
                <div className="admin__selfie admin__selfie--missing">no media</div>
              )}
              <div className="admin__verif-body">
                <div className="admin__rowhead">
                  <strong>{v.display_name ?? v.user_id}</strong>
                  <span className="admin__pill">{v.status}</span>
                  <span className="admin__time">{new Date(v.created_at).toLocaleString()}</span>
                </div>
                <p className="admin__meta">
                  Pose asked: <strong>{v.pose_key}</strong>
                  {typeof v.score === "number" ? ` · heuristic score ${v.score.toFixed(2)}` : ""}
                </p>
                {v.reason && <p className="admin__meta">Reason: {v.reason}</p>}
                {v.status === "pending" && (
                  <>
                    <input
                      type="text"
                      value={reasons[v.id] ?? ""}
                      onChange={(e) => setReasons((m) => ({ ...m, [v.id]: e.target.value }))}
                      placeholder={C.reason_placeholder}
                    />
                    <div className="admin__actions">
                      <Button
                        size="sm"
                        variant="primary"
                        loading={busyId === v.id}
                        onClick={() => void decide(v.id, "approve")}
                      >
                        {C.approve}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={busyId === v.id}
                        onClick={() => void decide(v.id, "reject")}
                      >
                        {C.reject}
                      </Button>
                    </div>
                  </>
                )}
                {v.status === "verified" && (
                  <div className="admin__actions">
                    <Button
                      size="sm"
                      variant="danger"
                      loading={busyId === v.id}
                      onClick={() => void decide(v.id, "revoke")}
                    >
                      {C.revoke}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
          {(rows ?? []).length === 0 && <p className="admin__empty">{C.empty}</p>}
        </div>
      )}
    </div>
  );
}
